import * as React from 'react';
import { Button as MatButton, ButtonProps as MatButtonProps, SxProps, Theme } from '@mui/material';
import { Icon, IconProps, IconKey } from '../Icon';

type Color = 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
type Size = 'small' | 'medium' | 'large';
type IconPosition = 'left' | 'right';
type Type = 'submit' | 'reset' | 'button';
type Variant = 'outlined' | 'contained' | 'text';

export interface ButtonProps {
  disabled?: boolean;
  disableRipple?: boolean;
  color?: Color;
  size?: Size;
  iconPosition?: IconPosition;
  icon?: IconKey;
  id?: string;
  label?: string;
  component?: React.ElementType<MatButtonProps>;
  key?: number;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  type?: Type;
  form?: string;
  variant?: Variant;
  dataProp?: string;
  title?: string;
  className?: string;
  sx?: SxProps<Theme>;
}

const Button: React.FunctionComponent<ButtonProps & React.RefAttributes<HTMLButtonElement> & MatButtonProps> = React.forwardRef((props, ref) => {
  const { iconPosition, label, dataProp, disabled, ...rest } = props;
  const iconOrder = iconPosition === 'left' ? 1 : 2;
  const order = iconPosition === 'left' ? 2 : 1;
  const iconOnly = !rest.children;
  const iconSize = (): IconProps['fontSize'] => {
    if (rest.size === 'medium') {
      return 'small';
    }
    return rest.size;
  };
  const iconMargin = () => {
    if (iconOnly || !rest.icon) {
      return '0';
    }
    if (iconOrder === 1) {
      return `0 0 0 8px`;
    }
    return `0 8px 0 0`;
  };
  const calculatedIconMargin = iconMargin();

  const variantType = (): ButtonProps['variant'] => {
    const { color } = rest;
    if (color === 'primary') {
      return 'contained';
    }
    if (color === 'secondary') {
      return 'outlined';
    }
    return 'text';
  };

  return (
    <MatButton
      disableRipple
      variant={variantType()}
      {...rest}
      disabled={disabled}
      role="button"
      aria-label={label}
      ref={ref}
      data-selector={dataProp}
      aria-disabled={disabled}
    >
      {rest.icon && (
        <Icon
          icon={rest.icon}
          fontSize={iconSize()}
          style={{
            order: iconOrder
          }}
        />
      )}
      <span
        style={{
          margin: calculatedIconMargin,
          order
        }}
      >
        <span>{rest.children}</span>
      </span>
    </MatButton>
  );
});

Button.displayName = 'Button';

export { Button };
